import { useAppSelector } from '@/store';
import {
  useBulkMarketingReviewsMutation,
  usePublishMarketingListingMutation,
} from '@/store/api/services/marketing/marketing';
import type { ListingView } from '@/types';
import { authReducer } from '@bitstopco/bitstop-theme';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { LoadingButton } from '@mui/lab';
import { Button, DialogActions, DialogContent, Divider, Typography } from '@mui/material';
import toast from 'react-hot-toast';

const {
  selectors: { userInfo },
} = authReducer;

const PublishMarketingListing = ({ hide, props }: ModalChildrenProps) => {
  const item = props?.data as ListingView;
  const [publishListing, { isLoading: isPublishing }] = usePublishMarketingListingMutation();
  const [bulkReviews, { isLoading: isApproving }] = useBulkMarketingReviewsMutation();
  const userData = useAppSelector(userInfo);

  const hasAtm = item?.atm_ids?.length > 0;
  const isInReview = item?.marketing_status === 'in_review';
  const isLoading = isPublishing || isApproving;

  const handleSubmit = async () => {
    try {
      if (isInReview) {
        await bulkReviews({
          location_ids: [item.location_id],
          operator_id: item.operator_id,
          action: 'Approve',
        }).unwrap();
        toast.success(`${item.name ?? `Location ${item.location_id}`} approved for publishing.`);
      } else {
        const nameParts = [userData?.first_name, userData?.last_name].filter(Boolean);
        const user = nameParts.length > 0 ? nameParts.join(' ') : (userData?.email ?? '');
        await publishListing({ locationId: item.location_id, user }).unwrap();
        toast.success(`${item.name ?? `Location ${item.location_id}`} submitted for publishing.`);
      }
      hide();
    } catch (_err) {
      toast.error('Failed to submit listing for publishing.');
    }
  };

  return (
    <>
      <DialogContent sx={{ my: 2 }}>
        {hasAtm ? (
          <>
            <Typography variant="h6">
              {isInReview ? 'Approve Listing' : 'Publish Listing'}
            </Typography>
            <Typography mt={1} variant="body1">
              Are you sure you want to {isInReview ? 'approve' : 'publish'}{' '}
              <b>{item?.name ?? `Location ${item?.location_id}`}</b>?
            </Typography>
            <Typography mt={1} variant="body2" color="text.secondary">
              {isInReview
                ? 'This listing is awaiting publishing. Approving it will make it live.'
                : 'Publishing this listing will make it visible across all Bitstop maps and marketing channels.'}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h6">Machine Required</Typography>
            <Typography mt={1} variant="body1">
              A machine must be installed at this location before it can be published.
            </Typography>
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={hide}>Cancel</Button>
        {hasAtm && (
          <LoadingButton variant="contained" onClick={handleSubmit} loading={isLoading}>
            {isInReview ? 'Approve' : 'Publish'}
          </LoadingButton>
        )}
      </DialogActions>
    </>
  );
};

export default PublishMarketingListing;
